import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { useArticles } from "@/hooks/useArticles";
import hero from "@/assets/hero-omoss.webp";
import { formatDateFull } from "@/lib/format";

const Nyheter = () => {
  const items = useArticles("nyheter");
  const [featured, ...rest] = items;

  return (
    <>
      <SEO
        title="Nyheter om redovisning, lön & tillväxt | Cloud"
        description="Läs nyheter och insikter om bokföring, lön, deklaration, digitalisering och företagstillväxt från Cloud Redovisning i Umeå och Stockholm."
      />
      <PageHero
        eyebrow="Nyheter"
        title={
          <>
            Senast hos <span className="italic text-primary">Cloud</span>.
          </>
        }
        description="Här kan du läsa om vad som händer hos oss i Umeå och Stockholm — nya medarbetare, utmärkelser, branschnyheter och insikter som hjälper ditt företag växa."
        image={hero}
        imageAlt="Nyheter och insikter från Cloud Redovisning"
      />

      {/* Featured */}
      {featured && (
        <section className="py-16 md:py-20 bg-background">
          <div className="container-page">
            <Link
              to={`/nyheter/${featured.slug}`}
              className="group grid gap-10 lg:grid-cols-12 lg:gap-14 items-center"
            >
              <div className="lg:col-span-7">
                <div className="relative aspect-[16/10] overflow-hidden rounded-sm bg-background-deep">
                  <img
                    src={featured.hero}
                    alt={featured.title}
                    className="h-full w-full object-contain transition-transform duration-1000 group-hover:scale-[1.04]"
                    loading="eager"
                    fetchpriority="high"
                    width={1600}
                    height={1000}
                    decoding="async"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium tracking-wider uppercase bg-primary text-primary-foreground backdrop-blur-sm">
                      Senaste nytt
                    </span>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5">
                <time className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
                  <Calendar className="h-3 w-3" />
                  {formatDateFull(featured.date)}
                </time>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 leading-[1.05] tracking-[-0.02em] group-hover:text-primary transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-6 text-foreground-muted leading-relaxed">
                  {featured.excerpt}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm text-primary font-medium">
                  Läs hela artikeln
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-16 md:py-24 bg-background border-t border-border">
        <div className="container-page">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="eyebrow">— Alla nyheter</div>
              <h2 className="font-display text-3xl md:text-5xl text-foreground mt-6 leading-tight">
                Färska perspektiv från{" "}
                <span className="italic text-primary">vårt team</span>.
              </h2>
            </div>
            <p className="text-foreground-muted max-w-md">
              Bläddra genom våra senaste artiklar — från praktiska insikter till
              nyheter från byrån.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((n) => (
              <Link
                key={n.slug}
                to={`/nyheter/${n.slug}`}
                className="group h-full bg-card border border-border rounded-sm overflow-hidden flex flex-col transition-all hover:border-foreground/30 hover:shadow-soft"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-background-deep">
                  {n.hero && (
                    <img
                      src={n.hero}
                      alt={n.title}
                      loading="lazy"
                      width={1280}
                      height={800}
                      className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-[1.05]"
                    />
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <time className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
                    <Calendar className="h-3 w-3" />
                    {formatDateFull(n.date)}
                  </time>
                  <h3 className="font-display text-2xl text-foreground mt-3 leading-tight group-hover:text-primary transition-colors">
                    {n.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-muted leading-relaxed flex-1 line-clamp-3">
                    {n.excerpt}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm text-primary font-medium">
                    Läs mer om {n.title}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Få våra insikter direkt i inkorgen."
        body="Vi delar med oss av tips kring ekonomi, skatt och tillväxt — utan spam, utan fluff."
      />
    </>
  );
};

export default Nyheter;
